import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useMemo, useState } from 'react';
import SharedButton from './buttons/shared-button';

import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Divider from '@mui/material/Divider';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import InputLabel from '@mui/material/InputLabel';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';

export interface SharedGroupedMultiSelectOption {
  value: string;
  label: string;
  groupType: string;
}

interface SharedGroupedMultiSelectProps<
  T extends SharedGroupedMultiSelectOption,
> {
  options: T[];
  value: string[];
  onChange: (values: string[]) => void;
  height?: number;
  width?: number;
  disabledValues?: string[];
  checkboxTooltipLabel?: string;
}

export default function SharedGroupedMultiSelect<
  T extends SharedGroupedMultiSelectOption,
>({
  options,
  value,
  onChange,
  height,
  width,
  disabledValues = [],
  checkboxTooltipLabel = '',
}: SharedGroupedMultiSelectProps<T>) {
  const theme = useTheme();

  const groupedOptions = useMemo(() => {
    return options.reduce<Record<string, T[]>>((groups, option) => {
      if (!groups[option.groupType]) {
        groups[option.groupType] = [];
      }
      groups[option.groupType].push(option);
      return groups;
    }, {});
  }, [options]);

  const allGroupKeys = useMemo(
    () => Object.keys(groupedOptions).sort((a, b) => a.localeCompare(b)),
    [groupedOptions]
  );

  const [selectOpen, setSelectOpen] = useState(false);
  const [tempSelectedGroups, setTempSelectedGroups] =
    useState<string[]>(allGroupKeys);
  const [appliedGroups, setAppliedGroups] = useState<string[]>(allGroupKeys);

  const filteredGroups = useMemo(() => {
    const result: Record<string, T[]> = {};
    appliedGroups.forEach((group) => {
      if (groupedOptions[group]) {
        result[group] = groupedOptions[group];
      }
    });
    return result;
  }, [groupedOptions, appliedGroups]);

  const handleDropdownSelectAll = () => {
    if (tempSelectedGroups.length === allGroupKeys.length) {
      setTempSelectedGroups([]);
    } else {
      setTempSelectedGroups(allGroupKeys);
    }
  };

  const handleDropdownGroupToggle = (group: string) => {
    setTempSelectedGroups((prev) =>
      prev.includes(group) ? prev.filter((g) => g !== group) : [...prev, group]
    );
  };

  const handleApplyFilters = () => {
    setAppliedGroups(tempSelectedGroups);
    setSelectOpen(false);
  };

  const handleResetFilters = () => {
    setTempSelectedGroups(allGroupKeys);
  };

  const handleSelectAll = () => {
    const togglableValues = options
      .filter((option) => !disabledValues.includes(option.value))
      .map((option) => option.value);

    onChange([...new Set([...value, ...togglableValues])]);
  };

  const handleReset = () => {
    onChange(
      value.filter((selectedValue) => disabledValues.includes(selectedValue))
    );
  };

  const handleGroupReset = (groupOptions: T[]) => {
    const togglableGroupValues = new Set(
      groupOptions
        .filter((option) => !disabledValues.includes(option.value))
        .map((option) => option.value)
    );
    onChange(
      value.filter((selectedValue) => !togglableGroupValues.has(selectedValue))
    );
  };

  const handleGroupChange = (groupOptions: T[], checked: boolean) => {
    const togglableValues = groupOptions
      .filter((option) => !disabledValues.includes(option.value))
      .map((option) => option.value);

    if (checked) {
      onChange([...new Set([...value, ...togglableValues])]);
      return;
    }

    onChange(
      value.filter((selectedValue) => !togglableValues.includes(selectedValue))
    );
  };

  const handleOptionChange = (optionValue: string, checked: boolean) => {
    if (checked) {
      onChange([...value, optionValue]);
      return;
    }
    onChange(value.filter((selectedValue) => selectedValue !== optionValue));
  };

  return (
    <Box
      sx={{
        p: 2,
        border: '0.5px solid',
        borderColor: theme.palette.semantic.surface.bg2,
        borderRadius: 1,
        backgroundColor: theme.palette.semantic.surface.bg2,
        height: height ?? '100%',
        width: width,
        overflowY: 'auto',
      }}
    >
      <Stack
        direction="row"
        spacing={2}
        sx={{
          alignItems: 'center',
          p: 2,
          mb: 2,
          justifyContent: 'space-between',
          borderRadius: '4px 4px 0px 0px',
          backgroundColor: theme.palette.semantic.surface.bg1,
          border: '0.5px solid',
          borderColor: theme.palette.semantic.outline.light,
        }}
      >
        <FormControl size="small" sx={{ minWidth: 250 }}>
          <InputLabel>Filter by Categories</InputLabel>

          <Select
            multiple
            open={selectOpen}
            onOpen={() => {
              setTempSelectedGroups(appliedGroups);
              setSelectOpen(true);
            }}
            onClose={() => setSelectOpen(false)}
            value={appliedGroups}
            label="Filter by Categories"
            renderValue={(selected) =>
              selected.length === allGroupKeys.length
                ? 'All Categories'
                : `${selected.length} Selected`
            }
            MenuProps={{
              PaperProps: {
                sx: {
                  mt: 0.5,
                  boxShadow: '0px 4px 12px rgba(0,0,0,0.1)',
                  backgroundColor: theme.palette.semantic.surface.bg1,
                  borderRadius: 1,
                  border: theme.palette.mode === 'dark' ? '1px solid' : 'none',
                  borderColor: theme.palette.semantic.outline.light,
                  '& .MuiMenuItem-root': {
                    minHeight: 28,
                    px: 1,
                    py: 0.5,
                  },
                  '& .MuiListItemText-primary': {
                    fontSize: '14px',
                  },
                },
              },
            }}
          >
            <MenuItem onClick={handleDropdownSelectAll}>
              <Checkbox
                checked={
                  tempSelectedGroups.length === allGroupKeys.length &&
                  allGroupKeys.length > 0
                }
              />
              <ListItemText primary="Select All Categories" />
            </MenuItem>

            <Divider
              sx={{
                mx: 1,
                color: theme.palette.semantic.outline.light,
              }}
            />

            {allGroupKeys.map((group) => (
              <MenuItem
                key={group}
                onClick={() => handleDropdownGroupToggle(group)}
              >
                <Checkbox checked={tempSelectedGroups.includes(group)} />
                <ListItemText
                  primary={`${group} (${groupedOptions[group].length})`}
                />
              </MenuItem>
            ))}

            <Box
              sx={{
                display: 'flex',
                p: 1,
                gap: 2,
              }}
              onMouseDown={(e) => e.stopPropagation()}
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.stopPropagation()}
            >
              <SharedButton
                variant="secondary"
                onClick={handleResetFilters}
                sx={{ flex: 0.8 }}
              >
                Reset
              </SharedButton>
              <SharedButton
                variant="primary"
                onClick={handleApplyFilters}
                sx={{ flex: 1.2 }}
              >
                Apply Filters
              </SharedButton>
            </Box>
          </Select>
        </FormControl>
        <Stack direction="row">
          <Button
            onClick={handleSelectAll}
            sx={{
              textTransform: 'none',
              textDecoration: 'underline',
              color: theme.palette.semantic.hyperlink.active,
            }}
          >
            Select All
          </Button>

          <Button
            onClick={handleReset}
            sx={{
              textTransform: 'none',
              textDecoration: 'underline',
              color: theme.palette.semantic.hyperlink.active,
            }}
          >
            Reset
          </Button>
        </Stack>
      </Stack>

      <Stack spacing={2}>
        {Object.entries(filteredGroups)
          .sort(([groupA], [groupB]) => groupA.localeCompare(groupB))
          .map(([group, groupOptions]) => {
            const togglableOptions = groupOptions.filter(
              (option) => !disabledValues.includes(option.value)
            );

            const selectedTogglableCount = togglableOptions.filter((option) =>
              value.includes(option.value)
            ).length;

            const totalSelectedCount = groupOptions.filter((option) =>
              value.includes(option.value)
            ).length;

            const allDisabled = togglableOptions.length === 0;

            const allSelected = allDisabled
              ? groupOptions.length > 0 &&
                totalSelectedCount === groupOptions.length
              : togglableOptions.length > 0 &&
                selectedTogglableCount === togglableOptions.length;

            const partiallySelected = totalSelectedCount > 0 && !allSelected;

            return (
              <Accordion
                key={group}
                disableGutters
                elevation={0}
                sx={{
                  border: '0.5px solid',
                  borderColor: theme.palette.semantic.outline.light,
                  borderRadius: 1,
                  overflow: 'hidden',
                  alignContent: 'center',
                  fontSize: '14px',
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  onClick={(event) => event.stopPropagation()}
                  sx={{
                    alignContent: 'center',
                    height: 52,
                    minHeight: 52,
                    backgroundColor: theme.palette.semantic.surface.bg1,
                    color: theme.palette.semantic.text.title2,
                    '& .MuiAccordionSummary-content': {
                      alignItems: 'center',
                    },
                    '&.Mui-expanded': {
                      minHeight: 52,
                    },
                  }}
                >
                  <Checkbox
                    checked={allSelected}
                    indeterminate={partiallySelected}
                    disabled={allDisabled}
                    onClick={(event) => event.stopPropagation()}
                    onChange={(event) =>
                      handleGroupChange(groupOptions, event.target.checked)
                    }
                  />

                  <Typography sx={{ fontWeight: 600 }}>
                    {group} ({groupOptions.length})
                  </Typography>
                </AccordionSummary>

                <AccordionDetails
                  sx={{
                    p: 0,
                    borderTop: '1px solid',
                    borderColor: theme.palette.semantic.outline.light,
                  }}
                >
                  <Stack>
                    {groupOptions.map((option) => {
                      const isDisabled = disabledValues.includes(option.value);
                      return (
                        <Tooltip
                          key={option.label}
                          title={isDisabled ? checkboxTooltipLabel : ''}
                        >
                          <Box sx={{ display: 'block', width: '100%' }}>
                            <FormControlLabel
                              key={option.value}
                              control={
                                <Checkbox
                                  checked={value.includes(option.value)}
                                  onChange={(event) =>
                                    handleOptionChange(
                                      option.value,
                                      event.target.checked
                                    )
                                  }
                                  disabled={isDisabled}
                                />
                              }
                              label={option.label}
                              sx={{
                                m: 0,
                                px: 2,
                                minHeight: 56,
                                borderBottom: '0.5px solid',
                                borderColor:
                                  theme.palette.semantic.outline.light,
                                width: '100%',
                              }}
                            />
                          </Box>
                        </Tooltip>
                      );
                    })}
                  </Stack>

                  <Button
                    onClick={() => handleGroupReset(groupOptions)}
                    sx={{
                      alignSelf: 'flex-start',
                      textTransform: 'none',
                      textDecoration: 'underline',
                      ml: 1.5,
                      px: 2,
                      py: 1.5,
                      minHeight: 52,
                      color: theme.palette.semantic.hyperlink.active,
                    }}
                  >
                    Reset
                  </Button>
                </AccordionDetails>
              </Accordion>
            );
          })}
      </Stack>
    </Box>
  );
}
